if (!customElements.get('marame-sticky-purchase')) {
  customElements.define('marame-sticky-purchase', class MarameStickyPurchase extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (this.productInfo?.dataset.originalSection || this.closest('quick-add-modal')) return;
      this.form = this.productInfo?.querySelector('product-form form');
      this.mainButton = this.form?.querySelector('[name="add"]');
      this.button = this.querySelector('[data-sticky-submit]');
      this.vtoLink = this.querySelector('[data-sticky-vto]');
      this.guideButton = this.querySelector('[data-sticky-guide]');
      this.scrollUnlocked = false;
      this.immersiveSection = document.querySelector('marame-cross-sells');
      if (!this.form || !this.mainButton || !this.button) return;

      this.events = new AbortController();
      this.confirmedSize = '';
      this.keyboardInteraction = false;
      this.footer = document.querySelector('footer');
      this.infoPanel = this.productInfo.querySelector('.product__info-container');
      this.header = document.querySelector('.section-header');
      const options = { signal: this.events.signal };
      this.button.addEventListener('click', () => this.activate(), options);
      this.vtoLink?.addEventListener('click', (event) => this.activateTryOn(event), options);
      this.guideButton?.addEventListener('click', () => {
        const source = this.productInfo.querySelector('[data-guide-open]');
        if (!source || source.hidden || this.hasOverlay()) return;
        source.click();
        this.returnFocusAfterOverlay = true;
        this.overlayReturnTarget = this.guideButton;
        this.schedule();
      }, options);
      this.productInfo.addEventListener('change', (event) => this.confirmSize(event), options);
      this.productInfo.addEventListener('marame:size-confirmed', (event) => {
        if (event.detail?.name === this.dataset.sizeOption) this.confirmedSize = event.detail.value;
        this.schedule();
      }, options);
      // Clicking the initially checked radio still counts as an intentional size choice.
      this.productInfo.addEventListener('click', (event) => this.confirmSize(event), options);
      document.addEventListener('scroll', () => this.schedule(), { ...options, passive: true, capture: true });
      document.addEventListener('focusin', () => this.sync(), options);
      document.addEventListener('focusout', () => this.schedule(), options);
      document.addEventListener('pointerdown', (event) => this.setInteractionMode(event), { ...options, capture: true, passive: true });
      document.addEventListener('keydown', (event) => this.setInteractionMode(event), { ...options, capture: true });
      window.addEventListener('resize', () => this.schedule(), { ...options, passive: true });
      window.visualViewport?.addEventListener('resize', () => this.schedule(), { ...options, passive: true });
      window.visualViewport?.addEventListener('scroll', () => this.schedule(), { ...options, passive: true });

      this.productObserver = new MutationObserver((records) => {
        if (records.some(({ target }) => !this.contains(target))) this.schedule();
      });
      this.productObserver.observe(this.productInfo, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'aria-hidden', 'class', 'value', 'hidden', 'inert', 'href'],
      });
      this.pageObserver = new MutationObserver((records) => {
        if (records.some((record) => this.pageMutationMatters(record))) this.schedule();
      });
      this.pageObserver.observe(document.body, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['open', 'class', 'aria-hidden', 'style', 'inert'],
      });
      // Product dialogs are inside product-info: open/close must be observed too.
      this.dialogObserver = new MutationObserver(() => this.schedule());
      this.dialogObserver.observe(this.productInfo, { subtree: true, attributes: true, attributeFilter: ['open'] });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.schedule());
        [this.infoPanel, this.header, this.mainButton].filter(Boolean).forEach((node) => this.resizeObserver.observe(node));
      }
      this.productInfo.setAttribute('data-marame-sticky-purchase-ready', '');
      this.sync();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.productObserver?.disconnect();
      this.pageObserver?.disconnect();
      this.dialogObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.focusReturnFrame) cancelAnimationFrame(this.focusReturnFrame);
      this.focusReturnFrame = null;
      this.productInfo?.removeAttribute('data-marame-sticky-purchase-ready');
      this.infoPanel?.style.removeProperty('--marame-product-sticky-top');
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
    }

    setInteractionMode(event) {
      if (event.type === 'pointerdown') this.keyboardInteraction = false;
      else if (!['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) this.keyboardInteraction = true;
      this.schedule();
    }

    pageMutationMatters(record) {
      const target = record.target;
      if (this.contains(target) || this.productInfo.contains(target)) return false;
      if (target === document.body && record.type === 'attributes') return true;
      const roots = 'dialog, modal-dialog, product-modal, quick-add-modal, cart-drawer, cart-notification, header-drawer, details-modal, [aria-modal="true"]';
      if (target.nodeType === 1 && target.closest(roots)) return true;
      if (record.type === 'attributes') return target.nodeType === 1 && Boolean(target.querySelector(roots));
      return [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 && (node.matches(roots) || node.querySelector(roots)));
    }

    selections() {
      return Array.from(this.productInfo.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown'), (group) => {
        const element = group.querySelector('input[type="radio"]:checked') || group.querySelector('select')?.selectedOptions[0];
        return { group, element, name: element?.dataset.optionName || '', value: element?.value || '' };
      });
    }

    confirmSize(event) {
      if (!event.target.closest('variant-selects')) return;
      let input = event.target;
      if (input.tagName === 'SELECT') {
        // Opening a select and cancelling it must not confirm its default value.
        if (event.type !== 'change') return;
        input = input.selectedOptions[0];
      } else {
        if (input.tagName === 'LABEL' || input.closest('label')) input = input.closest('label').control || input;
        if (input.tagName !== 'INPUT' || input.type !== 'radio') return;
      }
      if (input?.dataset.optionName === this.dataset.sizeOption && input.value && (input.checked || input.selected)) {
        this.confirmedSize = input.value;
      }
      this.schedule();
    }

    readState() {
      const selections = this.selections();
      const size = selections.find(({ name }) => name === this.dataset.sizeOption);
      const needsSize = Boolean(this.dataset.sizeOption && (!size || size.value !== this.confirmedSize));
      const needsPicker = needsSize || selections.some(({ element }) => !element);
      const variantId = this.form.querySelector('[name="id"]')?.value || '';
      const picker = this.productInfo.querySelector('variant-selects');
      let variant;
      try { variant = JSON.parse(picker?.querySelector('[data-selected-variant]')?.textContent || 'null'); } catch { variant = null; }
      const validVariant = Boolean(variantId && (!picker || String(variant?.id) === String(variantId)));
      const busy = this.mainButton.classList.contains('loading') || this.mainButton.getAttribute('aria-disabled') === 'true';
      const usesSizeSheet = this.dataset.figmaLayout === 'true' && this.productInfo.querySelector('marame-size-picker')?.dataset.enhanced === 'true';
      const nativeLabel = this.mainButton.querySelector('span')?.textContent.trim() || '';
      const label = busy ? this.dataset.loadingLabel : usesSizeSheet ? this.dataset.chooseSize : needsPicker ? (needsSize ? this.dataset.chooseSize : this.dataset.chooseOptions) : nativeLabel;
      const blocked = busy || (!usesSizeSheet && ((needsSize && !size) || (!needsPicker && (this.mainButton.disabled || !validVariant))));
      return { selections, needsSize, needsPicker, usesSizeSheet, variantId, busy, blocked, label };
    }

    isVisibleElement(element) {
      return Boolean(element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    }

    getOverlay() {
      return Array.from(document.querySelectorAll(
        'dialog[open], modal-dialog[open], product-modal[open], quick-add-modal[open], cart-drawer.active, #cart-notification.active, header-drawer details[open], details-modal details[open], [aria-modal="true"]'
      )).find((element) => this.isOpenOverlay(element));
    }

    isOpenOverlay(element) {
      if (!this.isVisibleElement(element) || element.closest(
        '[hidden], [inert], [aria-hidden="true"], details:not([open]), dialog:not([open]), modal-dialog:not([open]), product-modal:not([open]), quick-add-modal:not([open]), cart-drawer:not(.active), #cart-notification:not(.active)'
      )) return false;
      // Native open state also covers the first frame of an opening animation.
      if (element.matches('dialog[open], modal-dialog[open], product-modal[open], quick-add-modal[open], cart-drawer.active, #cart-notification.active, header-drawer details[open], details-modal details[open]')) return true;
      // aria-modal describes semantics, not visibility. Closed Dawn search still
      // has aria-modal and can retain a tiny layout rectangle inside its summary.
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const left = viewport?.offsetLeft || 0;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= top || rect.top >= top + (viewport?.height || window.innerHeight) || rect.right <= left || rect.left >= left + (viewport?.width || window.innerWidth)) return false;
      for (let node = element; node && node !== document.body; node = node.parentElement) {
        if (getComputedStyle(node).opacity === '0') return false;
      }
      return true;
    }

    hasOverlay() { return Boolean(this.getOverlay()); }

    isTyping() {
      const active = document.activeElement;
      if (!active || this.contains(active)) return false;
      const editing = active.matches('textarea, select, [contenteditable="true"], input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="hidden"])');
      const viewport = window.visualViewport;
      return editing || Boolean(viewport && viewport.scale <= 1.05 && window.innerHeight - viewport.height > 150);
    }

    shouldShow(overlay = this.hasOverlay()) {
      if (overlay || this.isTyping()) return false;
      // Never remove a keyboard-focused control merely because the page scrolled.
      if (this.keyboardInteraction && this.contains(document.activeElement) && this.dataset.visible === 'true') return true;
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const main = this.mainButton.getBoundingClientRect();
      const footer = this.footer?.getBoundingClientRect();
      const footerVisible = footer && footer.top < bottom && footer.bottom > top;
      if (this.dataset.figmaLayout === 'true') {
        if (window.scrollY > 10 || this.infoPanel?.scrollTop > 10) this.scrollUnlocked = true;
        const desktop = window.matchMedia('(min-width: 1024px)').matches;
        const immersive = this.immersiveSection?.getBoundingClientRect();
        const immersiveVisible = immersive && immersive.height > 0 && immersive.top < bottom && immersive.bottom > top;
        const desktopPane = desktop && this.productInfo?.dataset?.maramePdpLayout === 'columns';
        return Boolean((desktop || this.scrollUnlocked) && !footerVisible && (desktopPane || !immersiveVisible));
      }
      return this.isVisibleElement(this.mainButton) && main.bottom <= top && !footerVisible;
    }

    updateStickyTop() {
      if (this.dataset.figmaLayout === 'true') return;
      if (!this.infoPanel || !window.matchMedia('(min-width: 990px)').matches) return;
      const height = this.infoPanel.getBoundingClientRect().height;
      const headerBottom = Math.max(0, this.header?.getBoundingClientRect().bottom || 0);
      const top = Math.round(Math.min(headerBottom + 24, window.innerHeight - height - 24));
      const value = `${top}px`;
      if (this.infoPanel.style.getPropertyValue('--marame-product-sticky-top') !== value) {
        this.infoPanel.style.setProperty('--marame-product-sticky-top', value);
      }
    }

    setText(selector, text) {
      const element = this.querySelector(selector);
      if (element && element.textContent !== text) element.textContent = text;
    }

    setAttributeIfChanged(element, name, value) {
      if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    }

    getTryOnSource() {
      const container = this.productInfo.querySelector('[data-marame-vto]');
      const link = container?.querySelector('[data-marame-vto-link]');
      if (!link?.getAttribute('href') || container.hidden || container.inert || container.getAttribute('aria-busy') === 'true' || link.getAttribute('aria-disabled') === 'true') return null;
      if (link.closest('[hidden], [inert], [aria-hidden="true"]')) return null;
      const mirroredSource = this.dataset.figmaLayout === 'true' && container.dataset?.stickySource === 'true';
      if (!mirroredSource && !this.isVisibleElement(link)) return null;
      return link;
    }

    syncTryOn() {
      if (!this.vtoLink) return;
      const source = this.getTryOnSource();
      if (this.vtoLink.hidden !== !source) this.vtoLink.hidden = !source;
      if (!source) {
        if (this.vtoLink.hasAttribute('href')) this.vtoLink.removeAttribute('href');
        return;
      }
      this.setAttributeIfChanged(this.vtoLink, 'href', source.getAttribute('href'));
      const sourceNodes = Array.from(source.childNodes);
      if (this.vtoLink.childNodes.length !== sourceNodes.length || sourceNodes.some((node, index) => !node.isEqualNode(this.vtoLink.childNodes[index]))) {
        this.vtoLink.replaceChildren(...sourceNodes.map((node) => node.cloneNode(true)));
      }
    }

    activateTryOn(event) {
      // Mirror the live anchor again before default navigation, including a colour
      // update that completed before the next observer callback. No service loads here.
      if (!this.getTryOnSource() || this.hasOverlay() || this.isTyping()) { event.preventDefault(); this.syncTryOn(); return; }
      this.syncTryOn();
    }

    sync() {
      if (!this.mainButton?.isConnected) return;
      this.updateStickyTop();
      this.syncTryOn();
      if (this.guideButton) {
        const source = this.productInfo.querySelector('[data-guide-open]');
        const hidden = !source || source.hidden;
        if (this.guideButton.hidden !== hidden) this.guideButton.hidden = hidden;
      }
      const state = this.readState();
      this.setText('[data-sticky-label]', state.label);
      this.setAttributeIfChanged(this.button, 'aria-disabled', String(state.blocked));
      this.setAttributeIfChanged(this.button, 'aria-busy', String(state.busy));
      this.setText('[data-sticky-options]', state.selections
        .filter(({ name, value }) => value && !(state.needsSize && name === this.dataset.sizeOption))
        .map(({ name, value }) => `${name}: ${value}`).join(' · '));
      const priceRoot = this.productInfo.querySelector(`#price-${this.dataset.section}`);
      const price = priceRoot?.querySelector('.price--on-sale .price-item--sale') || priceRoot?.querySelector('.price__regular .price-item--regular');
      this.setText('[data-sticky-price]', !state.needsPicker && !state.variantId ? '' : price?.textContent.trim().replace(/\s+/g, ' ') || '');
      const nativeError = this.form.closest('product-form').querySelector('.product-form__error-message-wrapper');
      const error = nativeError && !nativeError.hidden ? nativeError.querySelector('.product-form__error-message')?.textContent.trim() || '' : '';
      this.setText('[data-sticky-error]', error);
      const errorMessage = this.querySelector('[data-sticky-error]');
      if (errorMessage.hidden !== !error) errorMessage.hidden = !error;
      const overlay = this.getOverlay();
      if (overlay && this.returnFocusAfterOverlay) this.previousOverlay = overlay;
      const show = this.shouldShow(Boolean(overlay));
      if (this.dataset.visible !== String(show)) this.dataset.visible = String(show);
      if (this.inert !== !show) this.inert = !show;
      this.setAttributeIfChanged(this, 'aria-hidden', String(!show));
      // Dawn returns focus synchronously when closing a drawer. An inert sticky
      // button cannot receive that first attempt; restore it once the drawer closes.
      if (!overlay && this.previousOverlay && this.returnFocusAfterOverlay) {
        const active = document.activeElement;
        const shouldRestore = active === document.body || this.previousOverlay.contains(active) || this.contains(active);
        const previousOverlay = this.previousOverlay;
        this.previousOverlay = null;
        this.returnFocusAfterOverlay = false;
        if (shouldRestore) this.restoreOverlayFocus(previousOverlay, this.overlayReturnTarget || this.button);
        this.overlayReturnTarget = null;
      }
    }

    restoreOverlayFocus(previousOverlay, target) {
      if (this.focusReturnFrame) cancelAnimationFrame(this.focusReturnFrame);
      this.focusReturnFrame = requestAnimationFrame(() => {
        this.focusReturnFrame = requestAnimationFrame(() => {
          this.focusReturnFrame = null;
          if (!this.isConnected || this.getOverlay() || this.returnFocusAfterOverlay) return;
          const active = document.activeElement;
          if (active !== document.body && !previousOverlay.contains(active) && !this.contains(active)) return;
          const returnTo = this.dataset.visible === 'true' && !this.inert ? target : this.mainButton;
          if (returnTo?.isConnected && !returnTo.closest('[inert], [hidden], [aria-hidden="true"]') && this.isVisibleElement(returnTo)) {
            returnTo.focus({ preventScroll: true });
          }
        });
      });
    }

    openSizePanel() {
      if (!this.productInfo.querySelector('marame-size-picker')?.openPanel()) return false;
      this.returnFocusAfterOverlay = true;
      this.overlayReturnTarget = this.button;
      this.schedule();
      return true;
    }

    focusPicker(state) {
      if (state.needsSize && this.openSizePanel()) return;
      const choice = state.selections.find(({ name }) => state.needsSize && name === this.dataset.sizeOption) || state.selections.find(({ element }) => !element) || state.selections[0];
      const group = choice?.group;
      const target = group?.querySelector('select, input:checked, input:not([disabled])') || this.mainButton;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Focus first without scrolling so a subsequent focus event cannot jump the page.
      target.focus({ preventScroll: true });
      (group || target).scrollIntoView({ block: 'center', behavior: reduceMotion ? 'instant' : 'smooth' });
      this.schedule();
    }

    activate() {
      // Read synchronously on every click: mutation callbacks may not have run yet.
      const state = this.readState();
      if (state.blocked || this.forwarding || this.hasOverlay() || this.isTyping()) return;
      // Figma's primary always reopens sizes, even after a choice or colour change.
      // The real Add action remains inside the sheet, where the customer can revise it.
      if (state.usesSizeSheet) {
        this.openSizePanel();
        return;
      }
      if (state.needsPicker) { this.focusPicker(state); return; }
      this.forwarding = true;
      this.returnFocusAfterOverlay = this.contains(document.activeElement);
      try {
        this.form.requestSubmit(this.mainButton);
      } finally {
        this.forwarding = false;
        this.sync();
      }
    }
  });
}
